import React, { useMemo, useContext } from 'react';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useHelpDeskAdmins from '@ubeya/shared/hooks/account/useHelpDeskAdmins';
import { SORT_BY_STRING } from '@ubeya/shared/utils/sorting';
import ConfigContext from '../../../contexts/ConfigContext';
import HelpDeskFlatMultiSelectFilter from './HelpDeskFlatMultiSelectFilter';
import useHelpDeskMultiSelectFilter from './useHelpDeskMultiSelectFilter';

const UNASSIGNED_KEY = '__unassigned__';
const toMapKey = (adminId) => (adminId == null ? UNASSIGNED_KEY : String(adminId));

const buildInitialMap = (defaultValue) => {
  if (!defaultValue) return {};
  const values = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
  return values.reduce((acc, item) => {
    const key = toMapKey(item?.adminId);
    acc[key] = { adminId: item?.adminId ?? null, label: item?.label || '' };
    return acc;
  }, {});
};

const HelpDeskAssigneesFilter = ({ defaultValue, handleClose, onApply, search, hideLayout = false }) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { admins } = useHelpDeskAdmins({ accountId });

  const initialMap = useMemo(() => buildInitialMap(defaultValue), [defaultValue]);
  const { selectedMap, handleToggle, clearSelection, applySelection } = useHelpDeskMultiSelectFilter(initialMap, {
    onApply,
    hideLayout,
    handleClose
  });

  const options = useMemo(() => {
    const noAssigneeLabel = t('help_desk_no_assignee');
    const list = (admins || [])
      .map((a) => {
        const label = a.name || a.email || `#${a.id}`;
        return { key: toMapKey(a.id), label, value: { adminId: a.id, label } };
      })
      .sort(SORT_BY_STRING('label'));
    return [{ key: UNASSIGNED_KEY, label: noAssigneeLabel, value: { adminId: null, label: noAssigneeLabel } }, ...list];
  }, [admins, t]);

  if (!options.length) return null;

  return (
    <HelpDeskFlatMultiSelectFilter
      name={t('help_desk_assignees')}
      options={options}
      selectedMap={selectedMap}
      onToggle={handleToggle}
      onClear={clearSelection}
      onApply={() => applySelection(selectedMap)}
      handleClose={handleClose}
      hideLayout={hideLayout}
      search={search}
    />
  );
};

export default HelpDeskAssigneesFilter;
